import { parseMoney } from '../money.js';
import { addDays, dayOfWeek, isISODate, mkDate, parts } from '../dates.js';
import { makeSplit } from './shared.js';
import { matchPayee } from './payees.js';
import { KeelError } from '../errors.js';
const WEEKDAYS = {
    sun: 0, sunday: 0, mon: 1, monday: 1, tue: 2, tues: 2, tuesday: 2, wed: 3, wednesday: 3,
    thu: 4, thur: 4, thurs: 4, thursday: 4, fri: 5, friday: 5, sat: 6, saturday: 6,
};
const MONTHS = {
    jan: 1, january: 1, feb: 2, february: 2, mar: 3, march: 3, apr: 4, april: 4, may: 5, jun: 6, june: 6,
    jul: 7, july: 7, aug: 8, august: 8, sep: 9, sept: 9, september: 9, oct: 10, october: 10, nov: 11, november: 11, dec: 12, december: 12,
};
const AMOUNT = /^([+-])?\$?((?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d{1,2})?|\.\d{1,2})$/;
/** ~10 years; larger "N days ago" values are rejected rather than producing absurd or invalid dates. */
export const MAX_DAYS_AGO = 3660;
const TRANSFER_WORDS = new Set(['transfer', 'xfer', 'tfr', 'trf']);
/** Valid calendar date or null (rejects 31/02 instead of rolling over). */
const safeDate = (y, m, d) => {
    if (m < 1 || m > 12 || d < 1 || d > 31)
        return null;
    const iso = mkDate(y, m, d);
    const p = parts(iso);
    return p.m === m && p.d === d && isISODate(iso) ? iso : null;
};
/** Year-less dates resolve to this year unless that is over a month in the future (then last year). */
const inferYear = (today, m, d) => {
    const y = parts(today).y;
    const cand = safeDate(y, m, d);
    if (cand && cand > addDays(today, 31))
        return safeDate(y - 1, m, d);
    return cand;
};
const ordinal = (s) => {
    const m = /^(\d{1,2})(?:st|nd|rd|th)?$/.exec(s);
    return m ? Number(m[1]) : null;
};
/** Try to read a date starting at token i. Returns the date and number of tokens consumed. */
const readDate = (tokens, i, ctx) => {
    const t = (tokens[i] ?? '').toLowerCase();
    const t1 = (tokens[i + 1] ?? '').toLowerCase();
    const t2 = (tokens[i + 2] ?? '').toLowerCase();
    const today = ctx.today;
    if (t === 'today' || t === 'tod')
        return { date: today, used: 1 };
    if (t === 'yesterday' || t === 'yday' || t === 'yest')
        return { date: addDays(today, -1), used: 1 };
    if (t === 'tomorrow' || t === 'tmr')
        return { date: addDays(today, 1), used: 1 };
    const back = (wd, strict) => {
        let n = (dayOfWeek(today) - wd + 7) % 7;
        if (n === 0 && strict)
            n = 7;
        return addDays(today, -n);
    };
    if (t === 'last' && WEEKDAYS[t1] !== undefined)
        return { date: back(WEEKDAYS[t1], true), used: 2 };
    if (WEEKDAYS[t] !== undefined)
        return { date: back(WEEKDAYS[t], false), used: 1 };
    if (/^\d+$/.test(t) && (t1 === 'days' || t1 === 'day') && t2 === 'ago') {
        const n = Number(t);
        if (n > MAX_DAYS_AGO)
            throw new KeelError('VALIDATION', `"${t} days ago" is too far back (max ${MAX_DAYS_AGO})`, { field: 'date' });
        return { date: addDays(today, -n), used: 3 };
    }
    let m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(t);
    if (m) {
        const d = safeDate(Number(m[1]), Number(m[2]), Number(m[3]));
        return d ? { date: d, used: 1 } : null;
    }
    m = /^(\d{1,2})[/.-](\d{1,2})(?:[/.-](\d{2}|\d{4}))?$/.exec(t);
    if (m) {
        const [a, b] = [Number(m[1]), Number(m[2])];
        const [dd, mm] = ctx.dateOrder === 'mdy' ? [b, a] : [a, b];
        const y = m[3] === undefined ? null : m[3].length === 2 ? 2000 + Number(m[3]) : Number(m[3]);
        const d = y === null ? inferYear(today, mm, dd) : safeDate(y, mm, dd);
        return d ? { date: d, used: 1 } : null;
    }
    const dn = ordinal(t);
    if (dn !== null && MONTHS[t1] !== undefined) {
        const d = inferYear(today, MONTHS[t1], dn);
        return d ? { date: d, used: 2 } : null;
    }
    const dn2 = ordinal(t1);
    if (MONTHS[t] !== undefined && dn2 !== null) {
        const d = inferYear(today, MONTHS[t], dn2);
        return d ? { date: d, used: 2 } : null;
    }
    return null;
};
/** Longest run of tokens (≤ 4) starting at i that names an open account: exact name first, then prefix/contains. */
const readAccount = (tokens, i, accounts, exactOnly = false) => {
    const open = accounts.filter((a) => !a.isClosed);
    for (const exact of exactOnly ? [true] : [true, false]) {
        for (let len = Math.min(4, tokens.length - i); len >= 1; len--) {
            const phrase = tokens.slice(i, i + len).join(' ').toLowerCase();
            const hit = open.find((a) => {
                const n = a.name.toLowerCase();
                return exact ? n === phrase : n.startsWith(phrase) || n.includes(` ${phrase}`);
            });
            if (hit)
                return { id: hit.id, used: len };
        }
    }
    return null;
};
/** Longest category name appearing as whole words in `text`. */
const findCategory = (text, categories) => {
    const lower = ` ${text.toLowerCase()} `;
    let best = null;
    for (const c of categories) {
        if (c.isHidden)
            continue;
        if (lower.includes(` ${c.name.toLowerCase()} `) && (!best || c.name.length > best.name.length))
            best = c;
    }
    return best;
};
/**
 * Natural-language entry: "coffee 4.50 yesterday #cafe", "+2500 salary 15/10", "woolies 86.20 groceries",
 * "transfer 500 to Savings", "lunch 18 @visa". Unsigned amounts are spending unless the category is income.
 */
export const quickAddParse = (text, ctx) => {
    let tokens = text.trim().split(/\s+/).filter(Boolean);
    const tagNames = tokens.filter((t) => t.startsWith('#') && t.length > 1).map((t) => t.slice(1));
    tokens = tokens.filter((t) => !(t.startsWith('#') && t.length > 1));
    let accountId = ctx.defaultAccountId;
    let transferAccountId;
    const isTransfer = tokens.length > 0 && TRANSFER_WORDS.has(tokens[0].toLowerCase());
    if (isTransfer)
        tokens = tokens.slice(1);
    const rest = [];
    let date;
    let amount;
    for (let i = 0; i < tokens.length;) {
        const tok = tokens[i];
        const lower = tok.toLowerCase();
        if (tok.startsWith('@') && tok.length > 1) {
            const acc = readAccount([tok.slice(1), ...tokens.slice(i + 1)], 0, ctx.accounts);
            if (acc) {
                accountId = acc.id;
                i += acc.used;
                continue;
            }
        }
        if (lower === 'to' || (lower === 'from' && isTransfer)) {
            // Outside an explicit "transfer", only an exact account name turns "to X" into a transfer.
            const acc = readAccount(tokens, i + 1, ctx.accounts, !isTransfer);
            if (acc) {
                if (lower === 'to')
                    transferAccountId = acc.id;
                else
                    accountId = acc.id;
                i += 1 + acc.used;
                continue;
            }
        }
        if (date === undefined) {
            const d = readDate(tokens, i, ctx);
            if (d) {
                date = d.date;
                i += d.used;
                continue;
            }
        }
        const m = amount === undefined ? AMOUNT.exec(tok) : null;
        if (m) {
            const cents = parseMoney(m[2]);
            if (!Number.isSafeInteger(cents))
                throw new KeelError('VALIDATION', 'That amount is too large', { field: 'amountCents' });
            amount = { cents, sign: m[1] ?? null };
            i++;
            continue;
        }
        rest.push(tok);
        i++;
    }
    const isXfer = isTransfer || transferAccountId !== undefined;
    const description = rest.join(' ');
    const category = isXfer ? null : findCategory(description, ctx.categories);
    const payeeText = category
        ? ` ${description} `.replace(new RegExp(` ${category.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')} `, 'i'), ' ').trim()
        : description;
    const payeeHit = payeeText !== '' && !isXfer ? matchPayee(payeeText, ctx.payees, 0.6) : null;
    const categoryId = category?.id ?? payeeHit?.payee.defaultCategoryId ?? null;
    const kind = category?.kind ?? (categoryId !== null ? ctx.categories.find((c) => c.id === categoryId)?.kind : undefined);
    let amountCents;
    if (amount) {
        const positive = amount.sign === '+' || (amount.sign === null && !isXfer && kind === 'income');
        amountCents = positive ? amount.cents : -amount.cents;
    }
    const lookup = new Map((ctx.tags ?? []).map((t) => [t.name.toLowerCase(), t.id]));
    const tagIds = tagNames.map((n) => lookup.get(n.toLowerCase())).filter((x) => x !== undefined);
    const out = {
        tagNames, isTransfer: isXfer,
        date: date ?? ctx.today,
        payeeRaw: payeeText || description || (category?.name ?? ''),
        memo: '', status: 'cleared', origin: 'manual',
        ...(accountId !== undefined ? { accountId } : {}),
        ...(transferAccountId !== undefined ? { transferAccountId } : {}),
        ...(payeeHit ? { payeeId: payeeHit.payee.id } : {}),
        ...(amountCents !== undefined ? { amountCents, splits: [makeSplit(categoryId, amountCents, { tagIds })] } : {}),
    };
    return out;
};
//# sourceMappingURL=quickAdd.js.map